import { useState, useEffect, useRef } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import {
  Package,
  MagnifyingGlass,
  ArrowsClockwise,
  Tag,
  X,
  Image as ImageIcon,
  Storefront,
  CaretUp,
  CaretDown,
  DownloadSimple,
  UploadSimple,
  Warning,
  PencilSimple,
  CalendarBlank
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatMoney = (value) => {
  if (value === null || value === undefined || isNaN(value)) return "—";
  return new Intl.NumberFormat("ru-RU", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value) + " ₽";
};

// Column header with sorting
const ColumnHeader = ({ children, sortable, sortKey, sortConfig, onSort, className = "" }) => {
  const isActive = sortConfig?.key === sortKey;
  
  const handleClick = () => {
    if (sortable && onSort) {
      onSort(sortKey);
    }
  };
  
  return (
    <TableHead 
      className={`text-zinc-600 text-xs font-medium ${sortable ? "cursor-pointer hover:text-zinc-900 select-none" : ""} ${className}`}
      onClick={handleClick}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortable && (
          isActive ? (
            sortConfig.direction === "asc" ? (
              <CaretUp size={14} weight="bold" className="text-[#005BFF]" />
            ) : (
              <CaretDown size={14} weight="bold" className="text-[#005BFF]" />
            )
          ) : (
            <span className="text-zinc-300 text-xs">⇅</span>
          )
        )}
      </div>
    </TableHead>
  );
};

const Products = ({ cachedData, onRefetchStats }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Cost upload
  const fileInputRef = useRef(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [uploadingCosts, setUploadingCosts] = useState(false);
  
  // Cost edit modal
  const [costEditModalOpen, setCostEditModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [newCostValue, setNewCostValue] = useState("");
  const [effectiveFromDate, setEffectiveFromDate] = useState("");
  const [savingCost, setSavingCost] = useState(false);
  
  // Pagination
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(15);
  
  // Filters
  const [search, setSearch] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [availableBrands, setAvailableBrands] = useState([]);
  
  // Sorting
  const [sortConfig, setSortConfig] = useState({ key: "offer_id", direction: "asc" });

  // === REACT QUERY CACHE INTEGRATION (добавлено для кэширования stats) ===
  useEffect(() => {
    if (cachedData?.stats) {
      setStats(cachedData.stats);
    }
  }, [cachedData]);
  // === END CACHE INTEGRATION ===

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchDebounced(search);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Load products
  useEffect(() => {
    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDebounced, selectedBrand, sortConfig, offset, limit]);

  // Load stats
  useEffect(() => {
    // Skip if using cached stats from wrapper
    if (cachedData?.stats) return;
    loadStats();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
        sort_by: sortConfig.key,
        sort_order: sortConfig.direction,
      });
      
      if (searchDebounced) params.append("search", searchDebounced);
      if (selectedBrand && selectedBrand !== "all") params.append("brand", selectedBrand);
      
      const response = await api.get(`/catalog?${params}`);
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
      setAvailableBrands(response.data.filters?.brands || []);
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Ошибка загрузки товаров");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get("/catalog/stats/summary");
      setStats(response.data);
    } catch (error) {
      console.error("Error loading stats:", error);
    }
  };

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      const response = await api.post("/catalog/refresh");
      toast.success(response.data.message);
      
      setTimeout(() => {
        loadProducts();
        loadStats();
        setRefreshing(false);
      }, 5000);
    } catch (error) {
      console.error("Error refreshing products:", error);
      toast.error("Ошибка обновления карточек");
      setRefreshing(false);
    }
  };

  // Download costs template with fallback method
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      // Use fetch to download and create blob for reliable file download
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/finance/costs/template`, {
        method: 'GET',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (!response.ok) {
        throw new Error('Ошибка скачивания');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'cost_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Шаблон скачан!");
      
    } catch (error) {
      console.error("Error downloading template:", error);
      toast.error("Ошибка скачивания шаблона. Попробуйте ещё раз.");
    } finally {
      setTimeout(() => setDownloadingTemplate(false), 500);
    }
  };

  // Upload costs file
  const handleUploadCosts = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingCosts(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/finance/costs/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const { updated, skipped } = response.data;
      
      if (updated > 0) {
        toast.success(
          <div>
            <div className="font-medium">Себестоимость загружена!</div>
            <div className="text-xs text-zinc-500 mt-1">
              Обновлено товаров: <span className="text-green-600">{updated}</span>
            </div>
            {skipped > 0 && (
              <div className="text-xs text-zinc-400">
                Пропущено (без новой цены): {skipped}
              </div>
            )}
          </div>,
          { duration: 5000 }
        );
        // Reload products to show updated costs
        loadProducts();
        loadStats();
      } else {
        toast.warning(
          <div>
            <div className="font-medium">Данные не загружены</div>
            <div className="text-xs text-zinc-500 mt-1">
              Заполните колонку &quot;НОВАЯ СЕБЕСТОИМОСТЬ&quot; в файле
            </div>
          </div>,
          { duration: 5000 }
        );
      }
      
    } catch (error) {
      console.error("Error uploading costs:", error);
      toast.error("Ошибка загрузки файла. Проверьте формат файла.");
    } finally {
      setUploadingCosts(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Open cost edit modal
  const handleOpenCostEdit = (product) => {
    setEditingProduct(product);
    setNewCostValue(product.cost_price ? String(product.cost_price) : "");
    // Default to today's date
    setEffectiveFromDate(new Date().toISOString().split('T')[0]);
    setCostEditModalOpen(true);
  };

  // Save cost from modal
  const handleSaveCost = async () => {
    if (!editingProduct || !newCostValue) {
      toast.error("Укажите себестоимость");
      return;
    }

    const costValue = parseFloat(newCostValue);
    if (isNaN(costValue) || costValue < 0) {
      toast.error("Некорректное значение себестоимости");
      return;
    }

    setSavingCost(true);
    try {
      const sku = editingProduct.sku;
      await api.post("/finance/costs/single", {
        sku: String(sku),
        cost: costValue,
        effective_from: effectiveFromDate || null
      });

      toast.success(
        <div>
          <div className="font-medium">Себестоимость сохранена</div>
          <div className="text-xs text-zinc-500 mt-1">
            {editingProduct.offer_id}: {costValue} ₽ с {effectiveFromDate}
          </div>
        </div>
      );

      // Update local state
      setProducts(prev => prev.map(p => 
        p.sku === sku ? { ...p, cost_price: costValue } : p
      ));

      setCostEditModalOpen(false);
    } catch (error) {
      console.error("Error saving cost:", error);
      toast.error("Ошибка сохранения себестоимости");
    } finally {
      setSavingCost(false);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
    }));
    setOffset(0);
  };

  const clearFilters = () => {
    setSearch("");
    setSelectedBrand("all");
    setOffset(0);
  };

  const hasFilters = search || selectedBrand !== "all";
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-chivo text-2xl font-bold text-zinc-900 flex items-center gap-2">
            <Storefront size={28} weight="bold" className="text-[#005BFF]" />
            Каталог товаров
          </h1>
          <p className="text-sm text-zinc-500">
            Управление карточками товаров из Ozon
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Cost management buttons */}
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className="border-green-500/50 text-green-600 hover:bg-green-50 hover:text-green-700"
          >
            <DownloadSimple size={18} className="mr-2" />
            {downloadingTemplate ? "Скачивание..." : "Скачать шаблон"}
          </Button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleUploadCosts}
            accept=".xlsx,.xls"
            className="hidden"
          />
          <Button
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCosts}
            className="border-[#005BFF]/50 text-[#005BFF] hover:bg-blue-50 hover:text-[#0047CC]"
          >
            <UploadSimple size={18} className="mr-2" />
            {uploadingCosts ? "Загрузка..." : "Загрузить себестоимость"}
          </Button>
          
          <Button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-[#005BFF] hover:bg-[#0047CC] text-white"
          >
            <ArrowsClockwise 
              size={18} 
              className={`mr-2 ${refreshing ? "animate-spin" : ""}`} 
            />
            {refreshing ? "Обновление..." : "Обновить карточки"}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-100">
            <CardContent className="p-3">
              <div className="text-xs text-blue-600 font-medium mb-1">Всего товаров</div>
              <div className="text-lg font-bold text-blue-700">{stats.total}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-green-50 to-white border-green-100">
            <CardContent className="p-3">
              <div className="text-xs text-green-600 font-medium mb-1">В наличии</div>
              <div className="text-lg font-bold text-green-700">{stats.in_stock}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-red-50 to-white border-red-100">
            <CardContent className="p-3">
              <div className="text-xs text-red-600 font-medium mb-1">Нет в наличии</div>
              <div className="text-lg font-bold text-red-700">{stats.out_of_stock}</div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-100">
            <CardContent className="p-3">
              <div className="text-xs text-purple-600 font-medium mb-1">Брендов</div>
              <div className="text-lg font-bold text-purple-700">{stats.brands_count}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass 
            size={18} 
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" 
          />
          <Input
            placeholder="Поиск по названию, артикулу, SKU, штрихкоду..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={selectedBrand} onValueChange={setSelectedBrand}>
          <SelectTrigger className="w-full md:w-48">
            <SelectValue placeholder="Бренд" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все бренды</SelectItem>
            {availableBrands.filter(brand => brand && brand.trim()).map((brand) => (
              <SelectItem key={brand} value={brand}>{brand}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {hasFilters && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            className="text-zinc-500 hover:text-zinc-700"
          >
            <X size={16} className="mr-1" />
            Сбросить
          </Button>
        )}
      </div>

      {/* Products Table */}
      <Card className="bg-white border border-zinc-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50 hover:bg-zinc-50">
                <ColumnHeader 
                  sortable 
                  sortKey="offer_id" 
                  sortConfig={sortConfig} 
                  onSort={handleSort}
                  className="min-w-[100px]"
                >
                  Артикул
                </ColumnHeader>
                <ColumnHeader 
                  sortable 
                  sortKey="name" 
                  sortConfig={sortConfig} 
                  onSort={handleSort}
                  className="min-w-[300px]"
                >
                  Товар
                </ColumnHeader>
                <ColumnHeader 
                  sortable 
                  sortKey="sku" 
                  sortConfig={sortConfig} 
                  onSort={handleSort}
                  className="min-w-[100px]"
                >
                  SKU
                </ColumnHeader>
                <TableHead className="text-zinc-600 text-xs font-medium min-w-[140px]">
                  Штрихкод
                </TableHead>
                <TableHead className="text-zinc-600 text-xs font-medium min-w-[100px]">
                  Бренд
                </TableHead>
                <TableHead className="text-zinc-600 text-xs font-medium min-w-[120px]">
                  Размеры (мм)
                </TableHead>
                <TableHead className="text-zinc-600 text-xs font-medium min-w-[80px] text-right">
                  Вес (г)
                </TableHead>
                <ColumnHeader 
                  sortable 
                  sortKey="cost_price" 
                  sortConfig={sortConfig} 
                  onSort={handleSort}
                  className="text-right min-w-[100px]"
                >
                  Себестоимость
                </ColumnHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-zinc-500">
                    <ArrowsClockwise size={24} className="inline animate-spin mr-2" />
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12">
                    <Package size={48} className="inline text-zinc-300 mb-2" />
                    <p className="text-lg font-medium text-zinc-600 mb-2">Товары не найдены</p>
                    <p className="text-sm text-zinc-400">Попробуйте изменить параметры поиска</p>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow 
                    key={product.sku} 
                    className="hover:bg-zinc-50"
                  >
                    <TableCell className="px-2 py-3">
                      <div className="flex flex-col">
                        <span className="font-mono text-sm font-medium text-[#005BFF]">
                          {product.offer_id || "—"}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-3">
                      <div className="flex items-start gap-3">
                        {product.primary_image ? (
                          <img 
                            src={product.primary_image} 
                            alt={product.name}
                            className="w-12 h-12 object-cover rounded border border-zinc-200 flex-shrink-0"
                            loading="lazy"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.nextSibling.style.display = 'flex';
                            }}
                          />
                        ) : null}
                        <div 
                          className={`w-12 h-12 bg-zinc-100 rounded flex items-center justify-center flex-shrink-0 ${product.primary_image ? 'hidden' : ''}`}
                        >
                          <Package size={20} className="text-zinc-400" />
                        </div>
                        <div className="min-w-[200px] max-w-[300px]">
                          <div className="text-sm font-medium text-zinc-900 leading-tight line-clamp-3" title={product.name}>
                            {product.name}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="px-2 py-3 font-mono text-sm text-zinc-500">
                      {product.sku}
                    </TableCell>
                    <TableCell className="px-2 py-3 font-mono text-xs text-zinc-500">
                      {product.barcode || "—"}
                    </TableCell>
                    <TableCell className="px-2 py-3">
                      {product.brand ? (
                        <Badge variant="secondary" className="bg-purple-100 text-purple-700 border-0">
                          {product.brand}
                        </Badge>
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2 py-3 font-mono text-xs text-zinc-600">
                      {product.dimensions && (product.dimensions.width || product.dimensions.height || product.dimensions.depth) ? (
                        `${product.dimensions.depth || 0}×${product.dimensions.width || 0}×${product.dimensions.height || 0}`
                      ) : (
                        <span className="text-zinc-400">—</span>
                      )}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-right font-mono text-sm text-zinc-600">
                      {product.dimensions?.weight ? product.dimensions.weight : <span className="text-zinc-400">—</span>}
                    </TableCell>
                    <TableCell className="px-2 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className={`font-medium ${product.cost_price ? 'text-zinc-900' : 'text-red-500'}`}>
                          {product.cost_price ? formatMoney(product.cost_price) : "Не задана"}
                        </span>
                        <button
                          onClick={() => handleOpenCostEdit(product)}
                          className="p-1 rounded hover:bg-zinc-100 text-zinc-400 hover:text-[#005BFF] transition-colors"
                          title="Изменить себестоимость"
                        >
                          <PencilSimple size={16} />
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-zinc-200">
          <div className="text-sm text-zinc-600">
            Показано <span className="font-medium">{Math.min(offset + limit, total)}</span> из <span className="font-medium">{total}</span> товаров
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-sm text-zinc-600">Отображать:</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setOffset(0);
                }}
                className="px-3 py-1.5 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value={15}>15 товаров</option>
                <option value={30}>30 товаров</option>
                <option value={60}>60 товаров</option>
                <option value={100}>100 товаров</option>
              </select>
            </div>
            
            {total > limit && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                >
                  Назад
                </Button>
                <span className="flex items-center px-3 text-sm text-zinc-500">
                  {currentPage} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                >
                  Вперед
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>

      {/* Cost Edit Modal */}
      <Dialog open={costEditModalOpen} onOpenChange={setCostEditModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Изменить себестоимость</DialogTitle>
            <DialogDescription>
              {editingProduct && (
                <span className="text-zinc-600">
                  {editingProduct.offer_id} — {editingProduct.name?.substring(0, 50)}
                  {editingProduct.name?.length > 50 && "..."}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="cost" className="text-right">
                Себестоимость
              </Label>
              <div className="col-span-3 relative">
                <Input
                  id="cost"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={newCostValue}
                  onChange={(e) => setNewCostValue(e.target.value)}
                  className="pr-8"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">₽</span>
              </div>
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="effectiveFrom" className="text-right">
                Дата начала
              </Label>
              <div className="col-span-3 relative">
                <CalendarBlank size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input
                  id="effectiveFrom"
                  type="date"
                  value={effectiveFromDate}
                  onChange={(e) => setEffectiveFromDate(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {editingProduct?.cost_price && (
              <div className="text-sm text-zinc-500 bg-zinc-50 p-3 rounded-lg">
                <span className="font-medium">Текущая себестоимость:</span> {formatMoney(editingProduct.cost_price)}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCostEditModalOpen(false)}>
              Отмена
            </Button>
            <Button 
              onClick={handleSaveCost} 
              disabled={savingCost || !newCostValue}
              className="bg-[#005BFF] hover:bg-[#0047CC]"
            >
              {savingCost ? "Сохранение..." : "Сохранить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Products;
